import React, { ReactNode, useCallback, useState } from "react";

import { faPen } from "@fortawesome/pro-solid-svg-icons/faPen";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Popover from "antd/es/popover";
import Tooltip from "antd/es/tooltip";
import cn from "classnames";
import { useIntl } from "react-intl";

import {
  EntitiesSupportingBulkEdit,
  EntityTypesSupportingBulkEdit,
} from "@mapmycustomers/shared/types/entity";
import { Button } from "@mapmycustomers/ui";

import styles from "./BulkEdit.module.scss";
import BulkEditForm from "./BulkEditForm";

interface Props {
  containsRestrictedEditItems?: boolean;
  disabled?: boolean;
  entityType: EntityTypesSupportingBulkEdit;
  onHide?: (updated: boolean) => void;
  selectedEntities: Set<EntitiesSupportingBulkEdit>;
  tooltip?: ReactNode;
}

const BulkEditButton: React.FC<Props> = ({
  containsRestrictedEditItems,
  disabled,
  entityType,
  onHide,
  selectedEntities,
  tooltip,
}) => {
  const intl = useIntl();
  const [popoverVisible, setPopoverVisibility] = useState<boolean>(false);

  const handleHide = useCallback(
    (updated) => {
      setPopoverVisibility(false);
      onHide?.(updated);
    },
    [onHide, setPopoverVisibility]
  );

  const handleVisibilityChange = useCallback(
    (visibility: boolean) => {
      if (!disabled || !visibility) {
        setPopoverVisibility(visibility);
      }
    },
    [disabled, setPopoverVisibility]
  );

  return (
    <Popover
      content={
        <BulkEditForm
          containsRestrictedEditItems={containsRestrictedEditItems}
          entities={selectedEntities}
          entityType={entityType}
          onHide={handleHide}
        />
      }
      destroyTooltipOnHide
      onVisibleChange={handleVisibilityChange}
      overlayClassName={styles.popoverContainer}
      placement="bottomLeft"
      title={intl.formatMessage(
        {
          id: "view.selectBar.bulkEdit.title",
          defaultMessage:
            "Bulk edit {count} {count, plural, one {{entityType, select, trips {drive} other {record}}} other {{entityType, select, trips {drives} other {records}}}}",
          description: "Bulk Edit button on a selected entities bar",
        },
        { count: selectedEntities.size, entityType }
      )}
      trigger="click"
      visible={popoverVisible && !disabled}
    >
      <Tooltip title={tooltip}>
        <Button
          className={cn({ [styles.pressed]: popoverVisible })}
          disabled={disabled}
          leftIcon={<FontAwesomeIcon icon={faPen} />}
          type="text"
        >
          {intl.formatMessage({
            id: "view.selectBar.button.bulkEdit",
            defaultMessage: "Bulk edit",
            description: "Bulk Edit button on a selected entities bar",
          })}
        </Button>
      </Tooltip>
    </Popover>
  );
};

export default BulkEditButton;
